import React from 'react';
import { Mail, Phone, MapPin, Linkedin, Twitter, Facebook } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    services: [
      { name: 'AI Solutions', href: '/services/ai-solutions', isRoute: true },
      { name: 'Software Development', href: '/services/software-development', isRoute: true },
      { name: 'Data Science', href: '/services/data-science', isRoute: true },
      { name: 'Cloud Services', href: '/services/cloud-services', isRoute: true }
    ],
    company: [
      { name: 'About Us', href: '/about', isRoute: true },
      { name: 'Products', href: '/products', isRoute: true },
      { name: 'Industries', href: '/industries', isRoute: true },
      { name: 'Careers', href: '/careers', isRoute: true },
      { name: 'Contact', href: '/contact', isRoute: true }
    ]
  };

  const socialLinks = [
    { icon: Linkedin, href: 'https://www.linkedin.com/company/oneit-solutions-llc/about/', name: 'LinkedIn' },
    { icon: Twitter, href: 'https://x.com/SolutionsO96022', name: 'X (Twitter)' },
    { icon: Facebook, href: 'https://facebook.com/oneitsolutions', name: 'Facebook' }
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <footer className="bg-[#1A3E6E] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div className="space-y-4">
            <img 
              src="/logo-oneit4.png" 
              alt="OneIT Solutions LLC" 
              className="logo-large bg-white px-4 py-3 rounded"
            />
            <p className="text-gray-400 text-sm">
              Empowering businesses with innovative AI solutions, software development, and world-class tech talent.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-[#0F2847] rounded-full flex items-center justify-center hover:bg-[#2563EB] transition-colors duration-200"
                    aria-label={social.name}
                  >
                    <IconComponent size={20} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Services</h3>
            <ul className="space-y-2">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  {link.isRoute ? (
                    <Link
                      to={link.href}
                      className="text-gray-400 hover:text-[#1A3E6E] transition-colors duration-200 text-sm"
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      onClick={(e) => scrollToSection(e, link.href)}
                      className="text-gray-400 hover:text-[#1A3E6E] transition-colors duration-200 text-sm"
                    >
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Company</h3>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  {link.isRoute ? (
                    <Link
                      to={link.href}
                      className="text-gray-400 hover:text-[#1A3E6E] transition-colors duration-200 text-sm"
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      onClick={(e) => scrollToSection(e, link.href)}
                      className="text-gray-400 hover:text-[#1A3E6E] transition-colors duration-200 text-sm"
                    >
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Mail className="text-[#1A3E6E] flex-shrink-0 mt-1" size={18} />
                <a href="mailto:contact@oneitsolutions.com" className="text-gray-400 hover:text-[#1A3E6E] transition-colors text-sm">
                  contact@oneitsolutions.com
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Phone className="text-[#1A3E6E] flex-shrink-0 mt-1" size={18} />
                <a href="tel:+15551234567" className="text-gray-400 hover:text-[#1A3E6E] transition-colors text-sm">
                  +1 (469) 604-4193
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="text-[#1A3E6E] flex-shrink-0 mt-1" size={18} />
                <span className="text-gray-400 text-sm">
                  4100 Alpha Rd, Ste 478<br />Dallas, TX 75244, USA
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#2563EB] pt-8 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              &copy; {currentYear} OneIT Solutions, LLC,. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <a href="#" className="text-gray-400 hover:text-[#1A3E6E] transition-colors text-sm">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-400 hover:text-[#1A3E6E] transition-colors text-sm">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
